import { useCallback, useMemo } from 'react';
import {
  PROFILE_SECTIONS_ADD_CHILD,
  PROFILE_SECTIONS_CHILDREN,
  PROFILE_SECTIONS_DELETE,
  PROFILE_SECTIONS_ENDPOINT,
  PROFILE_SECTIONS_HIDE,
  PROFILE_SECTIONS_REGISTER,
  PROFILE_SECTIONS_UNHIDE,
  PROFILE_SECTIONS_UPDATE,
  PROFILE_SECTIONS_ZONES_REGISTER,
  PROFILE_SECTIONS_ZONES_SELF
} from '../../../shared/constants/endpoints';
import useApiRequest from '../../../shared/hooks/useApiRequest';

const resolveMessage = (error, fallback) => error?.data?.message || error?.message || fallback;

const useSectionManagement = ({ request, storeToken, onToast }) => {
  const {
    execute: executeRead,
    loading: readLoading,
    error: readError
  } = useApiRequest();

  const {
    execute: executeMutate,
    loading: mutateLoading,
    error: mutateError
  } = useApiRequest();

  const fetchChildren = useCallback(async (sectionUuid) => {
    if (!sectionUuid || typeof request !== 'function') {
      return [];
    }

    const response = await executeRead(() => request(PROFILE_SECTIONS_CHILDREN(sectionUuid), {
      method: 'GET',
      requestName: 'hierarchy-section-children'
    }), {
      logModule: 'useSectionManagement.fetchChildren'
    });

    return Array.isArray(response?.data) ? response.data : [];
  }, [executeRead, request]);

  const fetchZones = useCallback(async (floorNumber) => {
    if (typeof request !== 'function') {
      return [];
    }
    const response = await executeRead(() => request(PROFILE_SECTIONS_ZONES_SELF(floorNumber), {
      method: 'GET',
      requestName: 'hierarchy-zone-list'
    }), {
      logModule: 'useSectionManagement.fetchZones'
    });
    return Array.isArray(response?.data) ? response.data : [];
  }, [executeRead, request]);

  const createSection = useCallback(async (payload, options = {}) => {
    const { skipErrorToast = false } = options;
    return executeMutate(() => request(PROFILE_SECTIONS_REGISTER, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      requestName: 'hierarchy-section-create'
    }), {
      logModule: 'useSectionManagement.createSection',
      onSuccess: () => onToast?.({ message: 'Section added successfully.', variant: 'success' }),
      onError: (error) => {
        if (!skipErrorToast) {
          onToast?.({ message: resolveMessage(error, 'Unable to add section.'), variant: 'error' });
        }
      }
    });
  }, [executeMutate, onToast, request]);

  const updateSection = useCallback(async (sectionUuid, payload) => {
    return executeMutate(() => request(PROFILE_SECTIONS_UPDATE(sectionUuid), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      requestName: 'hierarchy-section-update'
    }), {
      logModule: 'useSectionManagement.updateSection',
      onSuccess: () => onToast?.({ message: 'Section updated successfully.', variant: 'success' }),
      onError: (error) => onToast?.({ message: resolveMessage(error, 'Unable to update section.'), variant: 'error' })
    });
  }, [executeMutate, onToast, request]);

  const deleteSection = useCallback(async (sectionUuid) => {
    return executeMutate(() => request(PROFILE_SECTIONS_DELETE(sectionUuid), {
      method: 'POST',
      requestName: 'hierarchy-section-delete'
    }), {
      logModule: 'useSectionManagement.deleteSection',
      onSuccess: () => onToast?.({ message: 'Section and all its counters deleted.', variant: 'success' }),
      onError: (error) => onToast?.({ message: resolveMessage(error, 'Unable to delete section.'), variant: 'error' })
    });
  }, [executeMutate, onToast, request]);

  const setSectionVisibility = useCallback(async (sectionUuid, shouldHide) => {
    const endpoint = shouldHide ? PROFILE_SECTIONS_HIDE(sectionUuid) : PROFILE_SECTIONS_UNHIDE(sectionUuid);
    return executeMutate(() => request(endpoint, {
      method: 'POST',
      requestName: 'hierarchy-section-toggle'
    }), {
      logModule: 'useSectionManagement.setSectionVisibility',
      onError: (error) => onToast?.({ message: resolveMessage(error, 'Unable to update section status.'), variant: 'error' })
    });
  }, [executeMutate, onToast, request]);

  const addChild = useCallback(async (payload) => {
    return executeMutate(() => request(PROFILE_SECTIONS_ADD_CHILD, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      requestName: 'hierarchy-counter-create'
    }), {
      logModule: 'useSectionManagement.addChild',
      onError: (error) => onToast?.({ message: resolveMessage(error, 'Unable to add counter.'), variant: 'error' })
    });
  }, [executeMutate, onToast, request]);

  const setCounterVisibility = useCallback(async (counterUuid, shouldHide) => {
    const endpoint = shouldHide ? 'hide' : 'unhide';
    return executeMutate(() => request(PROFILE_SECTIONS_ENDPOINT(endpoint, counterUuid), {
      method: 'POST',
      requestName: 'hierarchy-counter-toggle'
    }), {
      logModule: 'useSectionManagement.setCounterVisibility',
      onError: (error) => onToast?.({ message: resolveMessage(error, 'Unable to update counter status.'), variant: 'error' })
    });
  }, [executeMutate, onToast, request]);

  const deleteCounter = useCallback(async (counterUuid) => {
    return executeMutate(() => request(PROFILE_SECTIONS_DELETE(counterUuid), {
      method: 'POST',
      requestName: 'hierarchy-counter-delete'
    }), {
      logModule: 'useSectionManagement.deleteCounter',
      onError: (error) => onToast?.({ message: resolveMessage(error, 'Unable to delete counter.'), variant: 'error' })
    });
  }, [executeMutate, onToast, request]);

  const createZone = useCallback(async (payload, options = {}) => {
    const { skipSuccessToast = false, skipErrorToast = false } = options;
    return executeMutate(() => request(PROFILE_SECTIONS_ZONES_REGISTER, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      requestName: 'hierarchy-zone-create'
    }), {
      logModule: 'useSectionManagement.createZone',
      onSuccess: () => {
        if (!skipSuccessToast) {
          onToast?.({ message: 'Zone added successfully.', variant: 'success' });
        }
      },
      onError: (error) => {
        if (!skipErrorToast) {
          onToast?.({ message: resolveMessage(error, 'Unable to add zone.'), variant: 'error' });
        }
      }
    });
  }, [executeMutate, onToast, request]);

  return useMemo(() => ({
    fetchChildren,
    fetchZones,
    createSection,
    updateSection,
    deleteSection,
    setSectionVisibility,
    addChild,
    setCounterVisibility,
    deleteCounter,
    createZone,
    loading: readLoading || mutateLoading,
    error: readError || mutateError
  }), [
    addChild,
    createSection,
    createZone,
    deleteCounter,
    deleteSection,
    fetchChildren,
    fetchZones,
    mutateError,
    mutateLoading,
    readError,
    readLoading,
    setCounterVisibility,
    setSectionVisibility,
    updateSection
  ]);
};

export default useSectionManagement;
